export const COMPASS_FAVORITES_KEY = "skillAgentCompassFavorites";

/** Clave localStorage por usuario CRM. */
export function compassFavoritesStorageKey(userId) {
  const uid = Number(userId);
  if (Number.isFinite(uid) && uid > 0) {
    return `${COMPASS_FAVORITES_KEY}:u${uid}`;
  }
  return COMPASS_FAVORITES_KEY;
}

function normalizeFavorite(raw) {
  if (!raw || typeof raw !== "object") return null;
  const kind =
    raw.kind === "meeting"
      ? "meeting"
      : raw.kind === "project"
        ? "project"
        : raw.kind === "client"
          ? "client"
          : null;
  if (!kind) return null;
  const id = Number(raw.id);
  if (!Number.isFinite(id) || id <= 0) return null;

  if (kind === "client") {
    const label = String(raw.label || "").trim() || `Cliente #${id}`;
    return { kind: "client", id, label };
  }

  const projectId = Number(raw.projectId || (kind === "project" ? id : 0));
  if (!Number.isFinite(projectId) || projectId <= 0) return null;
  const label =
    String(raw.label || "").trim() ||
    (kind === "meeting" ? `Reunión #${id}` : `Proyecto #${id}`);
  const orgBrand = raw.orgBrand ? String(raw.orgBrand) : null;
  return orgBrand
    ? { kind, id, projectId, label, orgBrand }
    : { kind, id, projectId, label };
}

export function favoriteKey(fav) {
  if (!fav) return "";
  return `${fav.kind}:${Number(fav.id)}`;
}

export function parseCompassFavorites(raw) {
  let list = raw;
  if (typeof raw === "string") {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const row of list) {
    const fav = normalizeFavorite(row);
    if (!fav) continue;
    const key = favoriteKey(fav);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(fav);
  }
  return out;
}

export function serializeCompassFavorites(list) {
  return JSON.stringify(parseCompassFavorites(list));
}

export function isCompassFavorite(list, kind, id) {
  const want = `${kind}:${Number(id)}`;
  return (list || []).some((f) => favoriteKey(f) === want);
}

export function toggleCompassFavorite(list, entry) {
  const fav = normalizeFavorite(entry);
  if (!fav) return parseCompassFavorites(list);
  const current = parseCompassFavorites(list);
  const key = favoriteKey(fav);
  const idx = current.findIndex((f) => favoriteKey(f) === key);
  if (idx >= 0) {
    return current.filter((_, i) => i !== idx);
  }
  return [...current, fav];
}

/** Tras renombrar un proyecto: actualiza el label en Favoritos. */
export function renameCompassProjectFavorite(list, projectId, label) {
  const pid = Number(projectId);
  const nextLabel = String(label || "").trim();
  if (!Number.isFinite(pid) || pid <= 0 || !nextLabel) {
    return parseCompassFavorites(list);
  }
  return parseCompassFavorites(list).map((f) => {
    if (
      f.kind === "project" &&
      (Number(f.id) === pid || Number(f.projectId) === pid)
    ) {
      return { ...f, id: pid, projectId: pid, label: nextLabel };
    }
    return f;
  });
}

/**
 * Alinea labels de proyectos favoritos con los nombres actuales del árbol
 * (por si Favoritos se quedó con un nombre viejo).
 */
export function syncCompassFavoriteLabelsFromRoots(list, rootGroups = []) {
  const nameByProjectId = new Map();
  const nameByCustomerId = new Map();
  for (const g of Array.isArray(rootGroups) ? rootGroups : []) {
    const cid = Number(g?.customerId);
    const cname = String(g?.customerName || "").trim();
    if (Number.isFinite(cid) && cid > 0 && cname) {
      nameByCustomerId.set(cid, cname);
    }
    for (const p of g?.projects || []) {
      const pid = Number(p?.id);
      const pname = String(p?.name || "").trim();
      if (Number.isFinite(pid) && pid > 0 && pname) {
        nameByProjectId.set(pid, pname);
      }
      const pcid = Number(p?.customerId);
      const pcname = String(p?.customerName || "").trim();
      if (Number.isFinite(pcid) && pcid > 0 && pcname) {
        nameByCustomerId.set(pcid, pcname);
      }
    }
  }
  return parseCompassFavorites(list).map((f) => {
    if (f.kind === "project") {
      const next = nameByProjectId.get(Number(f.id));
      if (next && next !== f.label) return { ...f, label: next };
    }
    if (f.kind === "client") {
      const next = nameByCustomerId.get(Number(f.id));
      if (next && next !== f.label) return { ...f, label: next };
    }
    return f;
  });
}

function defaultStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Carga favoritos del userId. Si la clave por usuario está vacía y existe la
 * clave legacy compartida, la migra una vez a este usuario.
 */
export function loadCompassFavorites(userId, storage = defaultStorage()) {
  try {
    const key = compassFavoritesStorageKey(userId);
    const raw = storage?.getItem?.(key);
    const scoped = parseCompassFavorites(raw || "[]");
    const uid = Number(userId);
    if (
      scoped.length === 0 &&
      Number.isFinite(uid) &&
      uid > 0 &&
      key !== COMPASS_FAVORITES_KEY
    ) {
      const legacy = parseCompassFavorites(
        storage?.getItem?.(COMPASS_FAVORITES_KEY) || "[]",
      );
      if (legacy.length) {
        saveCompassFavorites(legacy, uid, storage);
        try {
          storage?.removeItem?.(COMPASS_FAVORITES_KEY);
        } catch {
          /* ignore */
        }
        return legacy;
      }
    }
    return scoped;
  } catch {
    return [];
  }
}

export function saveCompassFavorites(list, userId, storage = defaultStorage()) {
  const next = parseCompassFavorites(list);
  try {
    storage?.setItem?.(
      compassFavoritesStorageKey(userId),
      serializeCompassFavorites(next),
    );
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function favoriteFromCompassNode(node) {
  if (!node) return null;
  if (node.kind === "client") {
    const id = Number(node.CustomerId || node.customerId);
    if (!Number.isFinite(id) || id <= 0) return null;
    return {
      kind: "client",
      id,
      label: String(
        node.label ||
          node.CustomerName ||
          node.customerName ||
          `Cliente #${id}`,
      ).trim(),
    };
  }
  if (node.kind === "project") {
    const projectId = Number(node.ProjectId || node.projectId);
    if (!Number.isFinite(projectId) || projectId <= 0) return null;
    return {
      kind: "project",
      id: projectId,
      projectId,
      label: String(node.label || `Proyecto #${projectId}`).trim(),
    };
  }
  if (node.kind === "meeting") {
    const id = Number(node.MeetingSessionId || node.meeting?.id);
    const projectId = Number(node.ProjectId || node.projectId);
    if (!Number.isFinite(id) || id <= 0) return null;
    if (!Number.isFinite(projectId) || projectId <= 0) return null;
    const orgBrand = node.OrgBrand || node.orgBrand || null;
    return {
      kind: "meeting",
      id,
      projectId,
      label: String(
        node.label || node.meeting?.title || `Reunión #${id}`,
      ).trim(),
      ...(orgBrand ? { orgBrand: String(orgBrand) } : {}),
    };
  }
  return null;
}
